import {
    UserProfile,
    ClientInfo,
    userProfileQuery,
    subscriptionInfoQuery,
    createUserProfileMutation,
} from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { createRef, createSignal, ReadonlySignal, SerializableInstance } from '@funtech-int/rival';
import { GraphQLError } from 'graphql';

import { GraphqlClient } from './graphqlClient';

interface FetchOptions {
    abortSignal?: AbortSignal;
}

export interface ProfileManager {
    userProfile: UserProfile | null;
    clientInfo: ClientInfo | null;
    isNewlyRegisteredUser: boolean;
    hasSubscription: ReadonlySignal<boolean>;
    billingFeatureNames: ReadonlySignal<string[]>;
    isInternal(this: void): boolean;
    hasSpecificSubscription(this: void, featureName: string): boolean;
    ensureProfile(this: void, errors: GraphQLError[], options?: FetchOptions): Promise<void>;
    updateSubscriptionInfo(this: void, options?: FetchOptions): Promise<void>;
}

export const profileManagerRef = createRef<ProfileManager>('profileManager');

interface CreateProfileManagerParams {
    graphqlClient: GraphqlClient;
    userProfile?: UserProfile | null;
    clientInfo?: ClientInfo | null;
}

interface ProfileManagerData {
    userProfile: any;
    clientInfo: any;
    isNewlyRegisteredUser: boolean;
}

export function createProfileManager(
    params: CreateProfileManagerParams
): SerializableInstance<ProfileManager, ProfileManagerData> {
    const { graphqlClient } = params;
    let { userProfile = null } = params;
    let { clientInfo = null } = params;

    let isNewlyRegisteredUser = false;

    const hasSubscription = createSignal(Boolean(userProfile?.hasSubscription));
    const billingFeatureNames = createSignal(userProfile?.billingFeatureNames ?? []);

    return {
        get userProfile() {
            return userProfile;
        },
        get clientInfo() {
            return clientInfo;
        },
        get isNewlyRegisteredUser() {
            return isNewlyRegisteredUser;
        },

        hasSubscription: hasSubscription.seal(),
        billingFeatureNames: billingFeatureNames.seal(),

        isInternal() {
            return Boolean(userProfile?.isInternal) || Boolean(clientInfo?.yandexNet);
        },
        hasSpecificSubscription(featureName) {
            return (
                Boolean(hasSubscription.get()) && billingFeatureNames.get().includes(featureName)
            );
        },

        async ensureProfile(errors, options) {
            const shouldCreateProfile =
                errors.some(
                    (error) => error?.extensions?.code === 'UserProfileRegistrationRequiredError'
                ) ?? false;

            if (!shouldCreateProfile) {
                return;
            }

            const createProfileResult = await graphqlClient.fetch(
                {
                    operation: createUserProfileMutation,
                },
                {
                    abortSignal: options?.abortSignal,
                }
            );

            const mutationResult = createProfileResult.data?.data?.create;

            if (mutationResult?.status !== 'SUCCESS' || mutationResult?.error) {
                throw new Error(
                    `${createUserProfileMutation.name} ${mutationResult?.error?.message}`
                );
            }

            const result = await graphqlClient.fetch(
                {
                    operation: userProfileQuery,
                },
                {
                    abortSignal: options?.abortSignal,
                }
            );

            userProfile = result.data ?? null;

            if (userProfile) {
                isNewlyRegisteredUser = true;
            }
        },

        async updateSubscriptionInfo(options) {
            const { data } = await graphqlClient.fetch(
                { operation: subscriptionInfoQuery },
                {
                    abortSignal: options?.abortSignal,
                }
            );

            hasSubscription.set(Boolean(data?.hasSubscription));
            billingFeatureNames.set(data?.billingFeatureNames ?? []);
        },

        ref() {
            return profileManagerRef;
        },
        hydrate(data) {
            userProfile = userProfileQuery.hydrate(data.userProfile) ?? null;
            clientInfo = data.clientInfo;
            isNewlyRegisteredUser = data.isNewlyRegisteredUser;

            hasSubscription.set(Boolean(userProfile?.hasSubscription));
            billingFeatureNames.set(userProfile?.billingFeatureNames ?? []);
        },
        toJSON() {
            return {
                userProfile,
                clientInfo,
                isNewlyRegisteredUser,
            };
        },
    };
}
